import { ItemsPerPage, Table } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { Row } from 'react-table'

import { SortDirection } from '../../../../../../enums/sortDirection'
import { getTableColumnIdDirection } from '../../../../../../utils/getTableColumnIdDirection'
import { InvoicesSortProperty } from '../../../../enums/invoicesSortProperty'
import { useInvoicesList } from '../../context/invoicesList'
import { useInvoicesListData } from '../../context/invoicesListData'
import { useInvoicesListFilters } from '../../context/invoicesListFilters'
import { InvoicesTableRow } from '../../types/invoicesTableRow'
import { InvoicesTableEmptyRowInfo } from './elements/InvoicesTableEmptyRowInfo'
import { useInvoicesTableColumns } from './hooks/useInvoicesTableColumns'

export const InvoicesTable = (): ReactElement => {
  const { columnsFilteringContainer, options, tableId } = useInvoicesList()
  const [{ pageSize, sortProperty, sortDirection }, setFilters] = useInvoicesListFilters()
  const { invoices, isLoading, onSelectRow, pagination } = useInvoicesListData()
  const { columns } = useInvoicesTableColumns()

  const sortedColumnId = useMemo(
    () => getTableColumnIdDirection(sortProperty, sortDirection === SortDirection.Desc),
    [sortDirection, sortProperty],
  )

  const handleSort = useCallback(
    (columnId: string, isDesc: boolean) => {
      setFilters({
        page: 1,
        sortProperty: columnId as InvoicesSortProperty,
        sortDirection: isDesc ? SortDirection.Desc : SortDirection.Asc,
      })
    },
    [setFilters],
  )

  const handlePageChange = useCallback(
    (page: number) => {
      setFilters({
        page,
      })
    },
    [setFilters],
  )

  const handleItemsPerPageChange = useCallback(
    (itemsPerPage: ItemsPerPage) => {
      setFilters({
        page: 1,
        pageSize: itemsPerPage,
      })
    },
    [setFilters],
  )

  const handleRowClick = useCallback(
    (row: Row<InvoicesTableRow>) => {
      onSelectRow(row.original)
    },
    [onSelectRow],
  )

  return (
    <Table
      columns={columns}
      columnsFilteringContainer={columnsFilteringContainer}
      currentPage={pagination?.page}
      data={invoices}
      emptyRowInfo={<InvoicesTableEmptyRowInfo />}
      id={tableId}
      isColumnsFilteringInContainer={options.showColumnsFilteringInActions}
      isLoading={isLoading}
      itemsPerPage={pageSize}
      onItemsPerPageChange={handleItemsPerPageChange}
      onPageChange={handlePageChange}
      onRowClick={handleRowClick}
      onSort={handleSort}
      sortedColumnId={sortedColumnId}
      totalPageItems={pagination?.total}
      withColumnsFiltering
      withItemsPerPageSelect
      withStickyHeader
    />
  )
}
